import { Badge, Button, Flex, Table, Text } from "@radix-ui/themes";
import { useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon } from "@radix-ui/react-icons";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { ExpandableText } from "@/v2/components/expandableText/ExpandableText";
import { useRegressionTestComparisonRows } from "@/routes/regression-tests/hooks/useRegressionTestComparisonRows";
import type { RegressionTestComparisonRow } from "@/routes/regression-tests/hooks/useRegressionTestComparisonRows";
import { formatRegressionRunMethodLabel } from "@/routes/regression-tests/regressionTestRunMethodLabel";
import styles from "./CompareRegressionTests.module.scss";

function formatSimilarityForExport(value: number | null): string {
  if (value == null) return "";
  return `${(value * 100).toFixed(1)}%`;
}

export function CompareRegressionTests() {
  const navigate = useNavigate();
  const { id: regressionSetId, id1: test1Id, id2: test2Id } = useParams();

  const { rows, isLoading, test1RunMethod, test2RunMethod } =
    useRegressionTestComparisonRows(
      regressionSetId ?? null,
      test1Id ?? null,
      test2Id ?? null
    );

  const label1 = useMemo(
    () => formatRegressionRunMethodLabel(test1RunMethod),
    [test1RunMethod]
  );
  const label2 = useMemo(
    () => formatRegressionRunMethodLabel(test2RunMethod),
    [test2RunMethod]
  );

  const handleBack = useCallback(() => {
    navigate("/automation-center/regression-tests");
  }, [navigate]);

  const exportToExcel = useCallback(() => {
    if (rows.length === 0) return;

    const buildRow = (row: RegressionTestComparisonRow) => ({
      Question: row.question,
      "Expected answer": row.expectedAnswer,
      [`${label1} test answer`]: row.test1Answer ?? "",
      [`${label1} test similarity`]: formatSimilarityForExport(row.test1Similarity),
      [`${label2} test answer`]: row.test2Answer ?? "",
      [`${label2} test similarity`]: formatSimilarityForExport(row.test2Similarity),
      [`${label1} test decision`]: row.test1Decision ?? "",
      [`${label2} test decision`]: row.test2Decision ?? "",
    });

    const excelData = rows.map(buildRow);
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Comparison");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });

    const dateStamp = new Date().toISOString().split("T")[0];
    const filename = `regression-compare-${regressionSetId?.slice(-8) ?? "set"}-${test1Id?.slice(-8) ?? "t1"}-${test2Id?.slice(-8) ?? "t2"}-${dateStamp}.xlsx`;
    saveAs(blob, filename);
  }, [rows, label1, label2, regressionSetId, test1Id, test2Id]);

  const paramsValid = Boolean(regressionSetId && test1Id && test2Id);

  const canExport = paramsValid && !isLoading && rows.length > 0;

  return (
    <Flex direction="column" gap="6" px="8" py="8" className={styles.root}>
      <Flex direction="row" gap="2" justify="between" align="start" wrap="wrap">
        <Flex direction="column" gap="2">
          <Flex align="center" gap="2">
            <Button variant="ghost" size="1" onClick={handleBack}>
              <ArrowLeftIcon />
            </Button>
            <Text size="5" weight="medium">
              Compare regression test results
            </Text>
          </Flex>
          <Text size="2" color="gray">
            Side-by-side answers, similarity, and decision for two test runs.
          </Text>
        </Flex>
        {canExport && (
          <Button variant="outline" onClick={exportToExcel}>
            Export to Excel
          </Button>
        )}
      </Flex>

      {!paramsValid && (
        <Text size="2" color="red">
          Missing set or test parameters. Use Compare from All Regression Tests.
        </Text>
      )}

      {paramsValid && isLoading && (
        <Text size="2" color="gray">
          Loading…
        </Text>
      )}

      {paramsValid && !isLoading && (
        <div className={styles.tableWrap}>
          <Table.Root>
            <Table.Header>
              <Table.Row className="text-gray-500 font-normal">
                <Table.ColumnHeaderCell className="font-normal min-w-[140px]">
                  Question
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell className="font-normal min-w-[140px]">
                  Expected answer
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell className="font-normal min-w-[160px]">
                  {label1} test answer
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell className="font-normal min-w-[100px]">
                  {label1} test similarity
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell className="font-normal min-w-[160px]">
                  {label2} test answer
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell className="font-normal min-w-[100px]">
                  {label2} test similarity
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell className="font-normal min-w-[120px]">
                  {label1} test decision
                </Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {rows.map((row) => (
                <Table.Row key={row.regressionSetItemId}>
                  <Table.Cell>
                    <ExpandableText text={row.question} />
                  </Table.Cell>
                  <Table.Cell>
                    <ExpandableText text={row.expectedAnswer} />
                  </Table.Cell>
                  <Table.Cell>
                    {row.test1Answer != null ? (
                      <ExpandableText text={row.test1Answer} />
                    ) : (
                      <Text size="2" color="gray">
                        —
                      </Text>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    {row.test1Similarity != null ? (
                      <Badge
                        color={
                          row.test1Similarity > 0.8
                            ? "green"
                            : row.test1Similarity > 0.6
                              ? "yellow"
                              : "red"
                        }
                        variant="soft"
                      >
                        {(row.test1Similarity * 100).toFixed(1)}%
                      </Badge>
                    ) : (
                      <Text size="2" color="gray">
                        —
                      </Text>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    {row.test2Answer != null ? (
                      <ExpandableText text={row.test2Answer} />
                    ) : (
                      <Text size="2" color="gray">
                        —
                      </Text>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    {row.test2Similarity != null ? (
                      <Badge
                        color={
                          row.test2Similarity > 0.8
                            ? "green"
                            : row.test2Similarity > 0.6
                              ? "yellow"
                              : "red"
                        }
                        variant="soft"
                      >
                        {(row.test2Similarity * 100).toFixed(1)}%
                      </Badge>
                    ) : (
                      <Text size="2" color="gray">
                        —
                      </Text>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <Text size="2">
                      {row.test1Decision ?? "—"}
                    </Text>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </div>
      )}
    </Flex>
  );
}
